import posthog from "posthog-js";
import { WebStorage, WebStorageNames, Common } from "../../../utils";
import { axiosBasic, getAuthToken, getUserInfo, initializeTokenRefresh, clearTokenRefresh } from "../../../api/axios";
import { config } from "../../../api/endPoints";
import { isSubscriptionSuspended, fetchSubscriptionData, getSubscriptionRestrictionMessage } from "../../../utils/subscriptionUtils";
import {
  LOGIN_FORM_CHANGED,
  IS_LOADING,
  LOG_OUT,
  ROLES,
  AUTHENTICATE_THE_USER_FALSE,
} from "./accountActionsTypes";
import { createHubConnection } from "../../realtime/hubActions";
import { HUB_CONNECTION } from "../../realtime/hubActionTypes";
import {
  applyAppLanguage,
  DEFAULT_PRIMARY_LANGUAGE,
  getPrimaryLanguageFromUserInfo,
  getStoredUserLanguage,
} from "../../../utils/appLanguages";
import { updatePrimaryLanguage } from "../../../api/userLanguageApi";
import { parseAuthResponseData } from "../../../utils/accountAuthUtils";
import { getAdminPostAuthPath } from "../../../utils/onboardingUtils";

const { Account } = config.REST_API;

/** Post-login routing. Super Admin always lands on platform /workplaces (same login screen). */
const ATTENDANCE_AGENT_BIND_RETURN_KEY = "hrmAttendanceAgentBindReturn";

const consumeAttendanceAgentBindReturnPath = () => {
  try {
    const path = sessionStorage.getItem(ATTENDANCE_AGENT_BIND_RETURN_KEY);
    if (!path || typeof path !== "string") return null;
    sessionStorage.removeItem(ATTENDANCE_AGENT_BIND_RETURN_KEY);
    if (!path.startsWith("/attendance-agent/bind")) return null;
    return path;
  } catch {
    return null;
  }
};

const navigateAfterAuth = ({
  roleId,
  companyInfo,
  isAnnualLeavePopupShown,
  navigation,
  dispatch,
  message,
  showSuccessMessage = false,
}) => {
  const bindReturn = consumeAttendanceAgentBindReturnPath();
  if (bindReturn && (roleId === 1 || roleId === 2)) {
    setTimeout(() => {
      navigation.push(bindReturn);
    }, 100);
    if (showSuccessMessage && message) Common.showSuccessMessage(message);
    return;
  }

  // Only touch SA surrogate flag for Super Admin — leave other roles' storage alone
  if (roleId === 1) {
    WebStorage.setLocalStore(WebStorageNames.SuperAdminInCompany, false);
    setTimeout(() => {
      navigation.push("/workplaces");
    }, 100);
    if (showSuccessMessage && message) Common.showSuccessMessage(message);
    return;
  }

  if (roleId === 2) {
    fetchSubscriptionData()
      .then((subscriptionData) => {
        if (subscriptionData && isSubscriptionSuspended(subscriptionData)) {
          setTimeout(() => {
            navigation.push("/settings/company?tab=billing");
          }, 100);
        } else {
          setTimeout(() => {
            navigation.push(getAdminPostAuthPath(companyInfo, isAnnualLeavePopupShown));
          }, 100);
          if (showSuccessMessage && message) Common.showSuccessMessage(message);
        }
      })
      .catch(() => {
        setTimeout(() => {
          navigation.push(getAdminPostAuthPath(companyInfo, isAnnualLeavePopupShown));
        }, 100);
        if (showSuccessMessage && message) Common.showSuccessMessage(message);
      });
    return;
  }

  const companySlug = companyInfo?.name;
  fetchSubscriptionData()
    .then((subscriptionData) => {
      if (subscriptionData && isSubscriptionSuspended(subscriptionData)) {
        const errorMessage = getSubscriptionRestrictionMessage(subscriptionData);
        Common.showErrorMessage(
          errorMessage ||
            "Access is currently unavailable. Your company's subscription is not active. Please contact support for assistance."
        );
        dispatch(loginFormChanged("isLoggedIn", false));
        dispatch(logOut());
        finishLogoutAndGoToAuth();
      } else {
        setTimeout(() => {
          if ((roleId === 3 || roleId === 4) && companySlug) {
            navigation.push(`/${companySlug}/offices`);
          } else {
            navigation.push("/dashboard");
          }
        }, 100);
        if (showSuccessMessage && message) Common.showSuccessMessage(message);
      }
    })
    .catch(() => {
      setTimeout(() => {
        if ((roleId === 3 || roleId === 4) && companySlug) {
          navigation.push(`/${companySlug}/offices`);
        } else {
          navigation.push("/dashboard");
        }
      }, 100);
      if (showSuccessMessage && message) Common.showSuccessMessage(message);
    });
};

export const loginFormChanged = (prop, value) => ({
  type: LOGIN_FORM_CHANGED,
  payload: { prop, value },
});

export const createAndStartHubConnection = () => {
  return (dispatch) => {
    let hubConnection = createHubConnection();


    if (hubConnection) {
      // Start the connection immediately
      hubConnection.start()
        .then(() => {
          dispatch({
            type: HUB_CONNECTION.STATE.CREATED,
            payload: hubConnection
          });
        })
        .catch(err => {
          if (typeof posthog !== "undefined" && posthog.capture) {
            posthog.capture("accountHubConnection", {
              type: "exception",
              success: false,
              reason: err?.message ?? "Failed to start hub connection",
            });
          }
        });
    } else {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("accountHubConnection", {
          type: "exception",
          success: false,
          reason: "No access token available",
        });
      }
    }
  };
};

export const isLoading = (value) => ({
  type: IS_LOADING,
  payload: value,
});
const setUserdata = (
  userInfo,
  authData,
  isLoggedIn,
  roleId,
  selectedCompany,
  isAnnualLeavePopupShown = false,
  dispatch = null
) => {
  const primaryLanguage =
    getPrimaryLanguageFromUserInfo(userInfo) || DEFAULT_PRIMARY_LANGUAGE;
  applyAppLanguage(primaryLanguage);
  WebStorage.setLocalStore(WebStorageNames.UserInfo, userInfo);
  WebStorage.setLocalStore(WebStorageNames.AuthInfo, authData);
  WebStorage.setLocalStore(WebStorageNames.UserLoggedIn, isLoggedIn);
  WebStorage.setLocalStore(WebStorageNames.RoleId, roleId);
  WebStorage.setLocalStore(WebStorageNames.SelectedCompany, selectedCompany);

  // Set annual leave popup flag based on role (only for roleId 2)
  if (roleId === 2) {
    WebStorage.setLocalStore(WebStorageNames.IsAnnualLeavePopupShown, JSON.stringify(isAnnualLeavePopupShown));
    if (dispatch) {
      dispatch({ type: "SET_ANNUAL_LEAVE_POPUP_SHOWN", payload: isAnnualLeavePopupShown });
    }
  }
};

export const onGoogleAuth =
  ({ credential, clientId, navigation }) =>
    (dispatch) => {
      dispatch(isLoading(true));
      axiosBasic
        .post(Account.GoogleAuth, {
          Credential: credential,
          ClientId: clientId,
        })
        .then((response) => {
          const { success, data, message } = response.data;
          if (success) {

            const parsed = parseAuthResponseData(data);
            const {
              userInfo,
              authData,
              roleId,
              companyInfo,
              isAnnualLeavePopupShown,
            } = parsed;
            setUserdata(userInfo, authData, true, roleId, companyInfo, isAnnualLeavePopupShown, dispatch);
            if (typeof posthog !== "undefined" && posthog.capture) {
              posthog.capture("onGoogleAuth", {
                type: "info",
                userId: userInfo.selfUserId,
                success: true,
                method: "google",
                roleId,
              });
            }
            localStorage.setItem(
              "selectedCompany",
              JSON.stringify(companyInfo)
            );
            dispatch(loginFormChanged("isLoggedIn", true));
            dispatch(createAndStartHubConnection());

            // Initialize automatic token refresh
            initializeTokenRefresh();

            navigateAfterAuth({
              roleId,
              companyInfo,
              isAnnualLeavePopupShown,
              navigation,
              dispatch,
              message,
              showSuccessMessage: true,
            });
            //window.location.reload(false);
          } else {
            if (typeof posthog !== "undefined" && posthog.capture) {
              posthog.capture("onGoogleAuth", { type: "exception", userId: null, success: false, method: "google", reason: message });
            }
            Common.showErrorMessage(message);
          }
        })
        .catch((error) => {
          if (typeof posthog !== "undefined" && posthog.capture) {
            posthog.capture("onGoogleAuth", { type: "exception", userId: null, success: false, method: "google", reason: error?.message || "network_error" });
          }
          Common.showErrorMessage(error);
        })
        .finally(() => {
          dispatch(isLoading(false));
        });
    };

export const onFacebookAuth =
  ({ accessToken, userID, navigation }) =>
    (dispatch) => {
      dispatch(isLoading(true));
      axiosBasic
        .post(Account.FacebookAuth, {
          AccessToken: accessToken,
          UserID: userID,
        })
        .then((response) => {
          const { success, data, message } = response.data;
          if (success) {

            const parsed = parseAuthResponseData(data);
            const {
              userInfo,
              authData,
              roleId,
              companyInfo,
              isAnnualLeavePopupShown,
            } = parsed;
            setUserdata(userInfo, authData, true, roleId, companyInfo, isAnnualLeavePopupShown, dispatch);
            if (typeof posthog !== "undefined" && posthog.capture) {
              posthog.capture("onFacebookAuth", {
                type: "info",
                userId: userInfo.selfUserId,
                success: true,
                method: "facebook",
                roleId,
              });
            }
            localStorage.setItem(
              "selectedCompany",
              JSON.stringify(companyInfo)
            );
            dispatch(loginFormChanged("isLoggedIn", true));
            dispatch(createAndStartHubConnection());

            // Initialize automatic token refresh
            initializeTokenRefresh();

            navigateAfterAuth({
              roleId,
              companyInfo,
              isAnnualLeavePopupShown,
              navigation,
              dispatch,
              message,
              showSuccessMessage: true,
            });
            //window.location.reload(false);
          } else {
            if (typeof posthog !== "undefined" && posthog.capture) {
              posthog.capture("onFacebookAuth", { type: "exception", userId: null, success: false, method: "facebook", reason: message });
            }
            Common.showErrorMessage(message);
          }
        })
        .catch((error) => {
          if (typeof posthog !== "undefined" && posthog.capture) {
            posthog.capture("onFacebookAuth", { type: "exception", userId: null, success: false, method: "facebook", reason: error?.message || "network_error" });
          }
          Common.showErrorMessage(error);
        })
        .finally(() => {
          dispatch(isLoading(false));
        });
    };

export const onLoginUser =
  ({ email, password, navigation }) =>
    (dispatch) => {
      dispatch(isLoading(true));
      axiosBasic
        .post(Account.LogIn, {
          Email: email,
          Password: password,
        })
        .then((response) => {
          const { success, data, message } = response.data;
          if (success) {

            const parsed = parseAuthResponseData(data, { email });
            const {
              userInfo,
              authData,
              roleId,
              companyInfo,
              isAnnualLeavePopupShown,
            } = parsed;
            setUserdata(userInfo, authData, true, roleId, companyInfo, isAnnualLeavePopupShown, dispatch);
            if (typeof posthog !== "undefined" && posthog.capture) {
              posthog.capture("onLoginUser", {
                type: "info",
                userId: userInfo.selfUserId,
                success: true,
                method: "email",
                roleId,
              });
            }
            localStorage.setItem(
              "selectedCompany",
              JSON.stringify(companyInfo)
            );
            dispatch(loginFormChanged("isLoggedIn", true));
            dispatch(createAndStartHubConnection());

            // Initialize automatic token refresh
            initializeTokenRefresh();

            navigateAfterAuth({
              roleId,
              companyInfo,
              isAnnualLeavePopupShown,
              navigation,
              dispatch,
              message,
              showSuccessMessage: true,
            });
          } else {
            if (typeof posthog !== "undefined" && posthog.capture) {
              posthog.capture("onLoginUser", { userId: null, success: false, method: "email", reason: message });
            }
            Common.showErrorMessage(message);
          }
        })
        .catch((error) => {
          if (typeof posthog !== "undefined" && posthog.capture) {
            posthog.capture("onLoginUser", { type: "exception", userId: null, success: false, method: "email", reason: error?.message || "network_error" });
          }
          Common.showErrorMessage(error);
        })
        .finally(() => {
          dispatch(isLoading(false));
        });
    };

export const logOut = () => ({
  type: LOG_OUT,
});

const clearStorage = () => {
  clearTokenRefresh();
  localStorage.clear();
  sessionStorage.clear();
};

const finishLogoutAndGoToAuth = () => {
  clearStorage();
  // Hard navigation: avoids sticky isLoggedIn/roleId races that redirected
  // Super Admin to /dashboard with the Login UI still mounted.
  window.location.assign("/auth");
};

export const onLogOut = () => (dispatch) => {
  const auth = getAuthToken();
  const refreshToken = auth?.refreshToken;
  const userInfo = getUserInfo();
  const userId = userInfo?.selfUserId ?? null;

  try {
    if (typeof posthog !== "undefined" && posthog.capture) {
      posthog.capture("onLogOut", { type: "info", userId });
    }
  } catch (_) {
    // ignore analytics failures during logout
  }

  try {
    Common.removeAllMessages();
  } catch (_) {
    // ignore toast cleanup failures
  }

  dispatch(logOut());
  dispatch(loginFormChanged("isLoggedIn", false));

  // Fire logout API before unload (keepalive survives the hard navigation).
  if (refreshToken) {
    const logoutUrl = `${config.ROOT_URL}${Account.LogOff}?refreshToken=${encodeURIComponent(refreshToken)}`;
    const headers = {};
    if (auth?.accessToken) {
      headers.Authorization = `Bearer ${auth.accessToken}`;
    }
    try {
      fetch(logoutUrl, { method: "GET", keepalive: true, headers }).catch(() => {});
    } catch (_) {
      // ignore — client session is cleared regardless
    }
  }

  finishLogoutAndGoToAuth();
};

export const roles = (roles) => ({
  type: ROLES,
  payload: roles,
});

export const onGetSuperAdminRoles = () => (dispatch) => {
  dispatch(isLoading(true));
  axiosBasic
    .get(Account.GetSuperAdminRoles)
    .then((response) => {
      const { success, data, message } = response.data;
      if (success) {
        const userRoles = [];
        userRoles.push(
          data.map((i) => ({ key: i.id, text: i.name, value: i.id }))
        );
        dispatch(roles(userRoles));
      } else {
        Common.showErrorMessage(message);
      }
    })
    .catch((error) => {
      Common.showErrorMessage(error);
    })
    .finally(() => {
      dispatch(isLoading(false));
    });
};

export const onRegisterBySuperAdmin =
  ({
    ownerName,
    email,
    password,
    roleId,
    companyName,
    registrationNumber,
    phoneNumber,
    address,
    navigation,
  }) =>
    (dispatch) => {
      dispatch(isLoading(true));
      axiosBasic
        .post(Account.RegisterByAdmins, {
          Email: email,
          Password: password,
          Name: ownerName,
          Role: roleId,
          Company: {
            Name: companyName,
            OwnerName: ownerName,
            RegistrationNumber: registrationNumber,
            Address: address,
            PhoneNumber: phoneNumber,
          },
        })
        .then((response) => {
          const { success, data, message } = response.data;
          if (success) {
            const parsed = parseAuthResponseData(data, { email });
            const { userInfo, authData, roleId: createdRoleId } = parsed;
            setUserdata(userInfo, authData, true, createdRoleId);
            // Creating a company keeps Super Admin on the platform (or lands new admin on workplaces)
            WebStorage.setLocalStore(WebStorageNames.SuperAdminInCompany, false);
            navigation.push("/workplaces");
            Common.showSuccessMessage(message);
          } else {
            Common.showErrorMessage(message);
          }
        })
        .catch((error) => {
          Common.showErrorMessage(error);
        })
        .finally(() => {
          dispatch(isLoading(false));
        });
    };

export const onRegister =
  ({
    ownerName,
    email,
    password,
    companyName,
    registrationNumber,
    phoneNumber,
    address,
    navigation,
  }) =>
    (dispatch) => {
      dispatch(isLoading(true));
      axiosBasic
        .post(Account.Register, {
          Email: email,
          Password: password,
          Name: ownerName,
          Role: 2,
          Company: {
            Name: companyName,
            OwnerName: ownerName,
            RegistrationNumber: registrationNumber,
            Address: address,
            PhoneNumber: phoneNumber,
          },
        })
        .then((response) => {
          const { success, data, message } = response.data;
          if (success) {
            const parsed = parseAuthResponseData(data, { email });
            const {
              userInfo,
              authData,
              roleId,
              companyInfo,
              isAnnualLeavePopupShown,
            } = parsed;

            // The language picked on the register page. A brand-new account has no saved
            // preference yet, so keep the visitor's choice instead of resetting to English.
            const chosenLanguage = getStoredUserLanguage();

            setUserdata(userInfo, authData, true, roleId, companyInfo, isAnnualLeavePopupShown, dispatch);

            if (chosenLanguage && chosenLanguage !== getPrimaryLanguageFromUserInfo(userInfo)) {
              applyAppLanguage(chosenLanguage);
              updatePrimaryLanguage(chosenLanguage).catch(() => {});
            }

            localStorage.setItem(
              "selectedCompany",
              JSON.stringify(companyInfo)
            );
            // Create hub connection after authentication is complete
            // Add small delay to ensure data is stored
            dispatch(loginFormChanged("isLoggedIn", true));
            dispatch(createAndStartHubConnection());

            if (typeof posthog !== "undefined" && posthog.capture) {
              posthog.capture("onRegister", { userId: userInfo.selfUserId, success: true, roleId });
            }
            if (roleId === 2) {
              navigation.push(getAdminPostAuthPath(companyInfo, isAnnualLeavePopupShown));
            }
            Common.showSuccessMessage(message);
          } else {
            if (typeof posthog !== "undefined" && posthog.capture) {
              posthog.capture("onRegister", { type: "exception", userId: null, success: false, reason: message });
            }
            Common.showErrorMessage(message);
          }
        })
        .catch((error) => {
          if (typeof posthog !== "undefined" && posthog.capture) {
            posthog.capture("onRegister", { type: "exception", userId: null, success: false, reason: error?.message || "network_error" });
          }
          Common.showErrorMessage(error);
        })
        .finally(() => {
          dispatch(isLoading(false));
        });
    };

// Call to update the user's annual leave popup flag (defaultLeaveBalance is optional/nullable on API)
export const updateUserAnnualLeaveFlag = (defaultLeaveBalance) => {
  const query = defaultLeaveBalance != null && defaultLeaveBalance !== "" ? `?defaultLeaveBalance=${defaultLeaveBalance}` : "";
  return axiosBasic.put(`${Account.UpdateUserAnnualLeaveFlag}${query}`);
};

export const onForgotPassword =
  ({ email, navigation }) =>
    (dispatch) => {
      dispatch(isLoading(true));
      axiosBasic
        .post(Account.ForgotPassword, {
          Email: email,
        })
        .then((response) => {
          const { success, message } = response.data;
          if (success) {
            if (typeof posthog !== "undefined" && posthog.capture) {
              posthog.capture("onForgotPassword", { userId: null, success: true });
            }
            Common.showSuccessMessage(message || "Password reset instructions have been sent to your email.");
            // Navigate back to login after a short delay
            setTimeout(() => {
              if (navigation) {
                navigation.push("/login");
              }
            }, 2000);
          } else {
            if (typeof posthog !== "undefined" && posthog.capture) {
              posthog.capture("onForgotPassword", { type: "exception", userId: null, success: false, reason: message });
            }
            Common.showErrorMessage(message || "Failed to send password reset email. Please try again.");
          }
        })
        .catch((error) => {
          const errorMessage =
            error.response?.data?.message ||
            error.message ||
            "An error occurred. Please try again.";
          if (typeof posthog !== "undefined" && posthog.capture) {
            posthog.capture("onForgotPassword", { type: "exception", userId: null, success: false, reason: errorMessage });
          }
          Common.showErrorMessage(errorMessage);
        })
        .finally(() => {
          dispatch(isLoading(false));
        });
    };